import { Info, Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'

const CardDetails = ({ card, handleInputChange, handleApplyToAllCards, isApplyingToAll }) => {
    const [showHeadlineConfirmation, setShowHeadlineConfirmation] = useState(false)
    const [showHeadlineApplyToAllSuccess, setShowHeadlineApplyToAllSuccess] = useState(false)
    const [showDescriptionConfirmation, setShowDescriptionConfirmation] = useState(false)
    const [showDescriptionApplyToAllSuccess, setShowDescriptionApplyToAllSuccess] = useState(false)

    const onConfirmApplyHeadline = async () => {
        await handleApplyToAllCards(card.id, 'headline')
        setShowHeadlineConfirmation(false)
        setShowHeadlineApplyToAllSuccess(true)

        setTimeout(() => {
            setShowHeadlineApplyToAllSuccess(false)
        }, 5000)
    }

    const onConfirmApplyDescription = async () => {
        await handleApplyToAllCards(card.id, 'description')
        setShowDescriptionConfirmation(false)
        setShowDescriptionApplyToAllSuccess(true)

        setTimeout(() => {
            setShowDescriptionApplyToAllSuccess(false)
        }, 5000)
    }

    return (
        <div className="tw:px-3 tw:pb-3">
            <div className="tw:pt-3 tw:flex tw:flex-col tw:gap-4">
                {/* Headline */}
                <div className="tw:max-w-lg">
                    <div className="tw:flex tw:items-center tw:justify-between tw:mb-2.5">
                        <label className="tw:font-semibold tw:text-white tw:text-sm">Headline</label>
                        <Button
                            variant="ghost"
                            className="tw:!text-sm tw:!p-0 tw:h-5 tw:text-blue-300 hover:tw:text-blue-100 tw:flex tw:items-center tw:gap-1"
                            onClick={() => setShowHeadlineConfirmation(true)}
                            disabled={!card.headline || isApplyingToAll}
                        >
                            {showHeadlineApplyToAllSuccess && <Check size={16} />}
                            <span>Apply to All Cards</span>
                        </Button>
                    </div>
                    <Input
                        type="text"
                        value={card.headline}
                        placeholder="Write a short headline"
                        onChange={(e) => handleInputChange(card.id, 'headline', e.target.value)}
                    />
                </div>

                {/* Description */}
                <div className="tw:mb-3 tw:max-w-lg">
                    <div className="tw:flex tw:items-center tw:justify-between tw:mb-2">
                        <div className="tw:flex tw:items-center tw:gap-2">
                            <label className="tw:font-semibold tw:text-white tw:text-sm">Description</label>
                            <Info size={14} className="tw:text-gray-400" />
                        </div>
                        <Button
                            variant="ghost"
                            className="tw:!text-sm tw:!p-0 tw:h-5 tw:text-blue-300 hover:tw:text-blue-100 tw:flex tw:items-center tw:gap-1"
                            onClick={() => setShowDescriptionConfirmation(true)}
                            disabled={!card.description || isApplyingToAll}
                        >
                            {showDescriptionApplyToAllSuccess && <Check size={16} />}
                            <span>Apply to All Cards</span>
                        </Button>
                    </div>
                    <Input
                        placeholder="Include additional details"
                        value={card.description}
                        onChange={(e) => handleInputChange(card.id, 'description', e.target.value)}
                    />
                </div>

                {/* Alt Text */}
                <div className="tw:mb-3">
                    <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
                        <label className="tw:font-semibold tw:text-white tw:text-sm">Image Alt Text</label>
                        <Info size={14} className="tw:text-gray-400" />
                    </div>
                    <div className="tw:mb-2">
                        <small className="tw:text-xs tw:leading-relaxed">
                            Alt text describes your image for people with visual impairments who use assistive
                            technology. The alt text that you include will be applied to this image on each supported
                            placement.{' '}
                            <a href="#" className="tw:text-blue-500">
                                Learn more
                            </a>
                        </small>
                    </div>
                    <div className="tw:relative">
                        <Textarea
                            placeholder="Include alternative text"
                            rows={2}
                            maxLength={800}
                            value={card.image_alt_text}
                            onChange={(e) => handleInputChange(card.id, 'image_alt_text', e.target.value)}
                        />
                        <div className="tw:absolute tw:bottom-2 tw:right-3 tw:text-gray-400 tw:text-xs">
                            {card.image_alt_text.length}/800
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationDialog
                isOpen={showHeadlineConfirmation}
                onClose={() => setShowHeadlineConfirmation(false)}
                onConfirm={onConfirmApplyHeadline}
                title="Apply Headline to All Cards"
                description={`Are you sure you want to apply the headline "${card.headline}" to all cards in the carousel? This action will override existing headlines.`}
                confirmText="Apply to All"
                variant="default"
                isProcessing={isApplyingToAll}
            />
            <ConfirmationDialog
                isOpen={showDescriptionConfirmation}
                onClose={() => setShowDescriptionConfirmation(false)}
                onConfirm={onConfirmApplyDescription}
                title="Apply Description to All Cards"
                description={`Are you sure you want to apply the description "${card.description}" to all cards in the carousel? This action will override existing descriptions.`}
                confirmText="Apply to All"
                variant="default"
                isProcessing={isApplyingToAll}
            />
        </div>
    )
}

export default CardDetails
